import i18next from "i18next";
import { z } from "zod";
import {
  FIELD_MAX_LENGTH,
  FIELD_MUST_BE_SELECT_AT_LEAST_PRODUCTS,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  TIKTOK,
  TIKTOK_CAROUSEL_REQUIRES_TIKTOK_PLACEMENT,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE,
  ADS_TIKTOK_PLACEMENT_TIKTOK,
  TIKTOK_PRODUCTS_CONFIGURATION_TYPE,
} from "../../../../../utils/constants";
import _ from "lodash";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../../../utils/regex";

function getIndicesMap(arrayOfStrings) {
  const map = {};
  arrayOfStrings.forEach((txt, i) => {
    if (!map[txt]) {
      map[txt] = [];
    }
    map[txt].push(i);
  });
  return map;
}

export const adTikTokBaseSchema = ({ campaign, indexAdGroup, indexAd }) =>
  z
    .object({
      platform: z.literal(TIKTOK),
      titles: z.any().optional(),
      callToAction: z.string().optional(),
      callToActions: z.array(z.any()).optional(),
      callToActionID: z.any().optional(),
      mediaUrls: z.array(z.any()),
      mediaData: z.array(z.any()),
      musicUrl: z.any().optional(),
      isCarousel: z.any().optional(),
      destinationUrls: z.any().optional(),
      instantForm: z.any().optional(),
      productsConfiguration: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      const basicPaths = ["adsGroups", indexAdGroup, "ads", indexAd];

      const objective = campaign.platformsData?.[TIKTOK]?.objective;
      const adGroup = campaign.adsGroups?.[indexAdGroup] || {};
      const isInstantFormLead =
        objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
        adGroup.destinationType ===
          ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE;
      const callToActionID = data.callToActionID;

      if (isInstantFormLead) {
        if (!data.instantForm?.id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t(FIELD_REQUIRED),
            path: [...basicPaths, "instantForm"],
          });
        }
        if (data.isCarousel) {
          const placements = adGroup.placements || [];
          if (!placements.includes(ADS_TIKTOK_PLACEMENT_TIKTOK)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(TIKTOK_CAROUSEL_REQUIRES_TIKTOK_PLACEMENT),
              path: [...basicPaths, "isCarousel"],
            });
          }
        }
      } else {
        if (
          !data?.destinationUrls?.length ||
          !data?.destinationUrls?.[0]?.value?.trim()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t(FIELD_REQUIRED),
            path: [...basicPaths, "destinationUrls", 0, "value"],
          });
        }
        if (
          !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
            encodeURI(data?.destinationUrls?.[0]?.value || "")
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
            path: [...basicPaths, "destinationUrls", 0, "value"],
          });
        }
      }

      if (
        objective !== ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION &&
        !callToActionID &&
        !data.callToAction
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "callToAction"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
      if (
        objective !== ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION &&
        callToActionID &&
        _.isEmpty(data.callToActions)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "callToActions"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      const isTikTokPost =
        data.mediaData?.[0]?.from === ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST;

      if (!isTikTokPost) {
        data.titles.forEach((title, index) => {
          if (!title?.value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "titles", index, "value"],
              message: i18next.t(FIELD_REQUIRED),
            });
          }

          if (
            title.value.length > ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[TIKTOK]
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "titles", index, "value"],
              message: i18next.t(FIELD_MAX_LENGTH, {
                value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[TIKTOK],
              }),
            });
          }
        });

        const titlesNormalized = data.titles?.map((item) =>
          item?.value ? item?.value?.trim()?.toLowerCase() : ""
        );
        const titlesMap = getIndicesMap(titlesNormalized);

        Object.entries(titlesMap).forEach(([, indexes]) => {
          if (indexes.length > 1) {
            indexes.forEach((i) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...basicPaths, "titles", i, "value"],
                message: i18next.t(FIELD_TEXT_REPEATED),
              });
            });
          }
        });

        const mediaUrls = data?.mediaUrls || [];
        if (!mediaUrls?.length || !mediaUrls[0]?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t(FIELD_REQUIRED),
            path: [...basicPaths, "mediaUrls", 0],
          });
        }

        if (data.isCarousel) {
          if (!data?.musicUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "musicUrl"],
              message: i18next.t(FIELD_REQUIRED),
            });
          }
        }
      }

      const campaignType = campaign.platformsData?.[TIKTOK]?.type;
      const isCatalogCampaignTikTok =
        objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
        campaignType === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION;

      if (isCatalogCampaignTikTok) {
        const config = data?.productsConfiguration || {};

        if (!config?.type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "productsConfiguration", "type"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          config?.type === TIKTOK_PRODUCTS_CONFIGURATION_TYPE.PRODUCT_SET &&
          !config?.productSetID
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "productsConfiguration", "productSetID"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          config?.type === TIKTOK_PRODUCTS_CONFIGURATION_TYPE.PRODUCT_LIST &&
          (_.isEmpty(config?.products) || config?.products?.length <= 2)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "productsConfiguration", "products"],
            message: i18next.t(FIELD_MUST_BE_SELECT_AT_LEAST_PRODUCTS, {
              value: 3,
            }),
          });
        }
      }
    });
